import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyCustomerForm, toCustomerCreate } from '../api/mappers';
import { useCreateCustomer } from '../hooks/use-customer-mutations';
import {
  customerCreateSchema,
  type CustomerCreateFormInput,
  type CustomerCreateFormOutput,
} from '../schemas/customer-form-schema';
import { customerEmailTaken } from '../schemas/customer-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'email',
  'firstName',
  'lastName',
  'phone',
  'acceptsMarketing',
  'tags',
  'notes',
]);

/** `body.tags.3` belongs to the tags field. */
const mapField = (field: string) => field.replace(/^tags\.\d+$/, 'tags');

export interface CreateCustomerDialogProps {
  storeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (customer: Customer) => void;
}

/** A staff-made record starts as a guest; it turns active once the shopper sets a password. */
export function CreateCustomerDialog({
  storeId,
  open,
  onOpenChange,
  onCreated,
}: CreateCustomerDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const message = useErrorMessage();
  const create = useCreateCustomer(storeId);
  const form = useForm<CustomerCreateFormInput, unknown, CustomerCreateFormOutput>({
    resolver: useI18nResolver(customerCreateSchema),
    defaultValues: emptyCustomerForm(),
  });

  const close = () => {
    onOpenChange(false);
    create.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const customer = await create.mutateAsync(toCustomerCreate(values));
      close();
      onCreated(customer);
    } catch (error) {
      if (customerEmailTaken(error)) {
        form.setError('email', { type: 'server', message: t('customers:errors.emailTaken') });
        return;
      }
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('customers:create.title')}</DialogTitle>
          <DialogDescription>{t('customers:create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.email')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers:fields.firstName')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers:fields.lastName')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.phone')}</FormLabel>
                  <FormControl>
                    <Input type="tel" autoComplete="off" placeholder="+12125550148" {...field} />
                  </FormControl>
                  <FormDescription>{t('customers:fields.phoneHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="acceptsMarketing"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                  <div className="flex flex-col gap-1">
                    <FormLabel>{t('customers:fields.acceptsMarketing')}</FormLabel>
                    <FormDescription>{t('customers:fields.acceptsMarketingHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.tags')}</FormLabel>
                  <FormControl>
                    <TagInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('customers:notes.tagsPlaceholder')}
                      removeLabel={(tag) => t('customers:notes.removeTag', { tag })}
                      max={50}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.notes')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? t('common:actions.saving') : t('customers:create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
